import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import KaiyleeChatPanel from '../KaiyleeChatPanel';
import OfferPreviewCard from '../OfferCard/components/OfferPreviewCard';
import ResizableWindow, {
  SIDEBAR_WIDTH,
  WINDOWS_TOP_OFFSET,
} from './ResizableWindow';
import buildViewOfferDetails from './buildViewOfferDetails';

const WORKSPACE_EDGE_GAP = 16;
const DESKTOP_BREAKPOINT = 1200;
const WIDE_WORKSPACE_WIDTH = 1040;
const MEDIUM_WORKSPACE_WIDTH = 900;
const MIN_WORKSPACE_WIDTH = 320;
const MIN_WORKSPACE_HEIGHT = 400;

const OFFER_PREVIEW_LABELS = {
  DRAFT: 'draft',
  PREVIOUS_OFFER: 'previous offer',
  OFFER_IDEA: 'offer idea',
};

export function formatOfferPreviewLabel(preview) {
  if (!preview) return '';
  const label = OFFER_PREVIEW_LABELS[preview.kind] || 'offer';
  const article = label === 'offer idea' || label === 'offer' ? 'an' : 'a';
  return `Previewing ${article} ${label} · ${preview.position} of ${preview.count}`;
}

const WORKSPACE_MODE_LABELS = {
  EDITING_DRAFT: 'Editing a draft',
  VIEWING_DRAFT: 'Viewing a draft',
  EDITING_OFFER: 'Editing an offer',
  VIEWING_OFFER: 'Viewing an offer',
};

export function getAvailableWorkspaceWidth(viewportWidth, fixedLeft) {
  return Math.max(MIN_WORKSPACE_WIDTH, viewportWidth - fixedLeft - WORKSPACE_EDGE_GAP);
}

export function getWorkspaceLayout(width) {
  if (width >= WIDE_WORKSPACE_WIDTH) return 'wide';
  if (width >= MEDIUM_WORKSPACE_WIDTH) return 'medium';
  return 'narrow';
}

export default function KaiyleeWorkspaceWindow({
  open,
  minimized,
  onClose,
  onMinimize,
  onRestore,
  draft,
  onDraftChange,
  businessId,
  onOfferDataRefresh,
  onConfigureOffer,
}) {
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === 'undefined' ? DESKTOP_BREAKPOINT : window.innerWidth,
    height: typeof window === 'undefined' ? 800 : window.innerHeight,
  }));
  const sidebarIsHidden = viewport.width < DESKTOP_BREAKPOINT;
  const fixedLeft = sidebarIsHidden ? WORKSPACE_EDGE_GAP : SIDEBAR_WIDTH;
  const width = getAvailableWorkspaceWidth(viewport.width, fixedLeft);
  const height = Math.max(MIN_WORKSPACE_HEIGHT, viewport.height - WINDOWS_TOP_OFFSET - 8);
  const [workspaceWidth, setWorkspaceWidth] = useState(width);
  const [hasUserResized, setHasUserResized] = useState(false);
  const [offerPreview, setOfferPreview] = useState(null);
  const [workspaceMode, setWorkspaceMode] = useState(null);
  const contextLabel = offerPreview
    ? formatOfferPreviewLabel(offerPreview)
    : WORKSPACE_MODE_LABELS[workspaceMode];
  const displayedOffer = offerPreview?.offer || draft;
  const details = useMemo(() => buildViewOfferDetails(displayedOffer), [displayedOffer]);
  const layout = hasUserResized ? getWorkspaceLayout(workspaceWidth) : 'wide';
  const isWide = layout === 'wide';
  const isNarrow = layout === 'narrow';
  const chatFlex = isNarrow
    ? '0 0 clamp(420px, 58vh, 560px)'
    : isWide
      ? '0 0 clamp(420px, 32%, 520px)'
      : '0 0 clamp(360px, 40%, 440px)';
  const chatMinWidth = isNarrow ? 0 : isWide ? 420 : 360;

  useEffect(() => {
    const updateViewport = () => setViewport({
      width: window.innerWidth,
      height: window.innerHeight,
    });
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, []);

  useEffect(() => {
    if (open) setWorkspaceWidth(width);
  }, [open, width]);

  useEffect(() => {
    setHasUserResized(false);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setOfferPreview(null);
      setWorkspaceMode(null);
    }
  }, [open]);

  return (
    <ResizableWindow
      id="kaiylee-workspace"
      title="Kaiylee™"
      subtitle="Marketing assistant"
      open={open}
      minimized={minimized}
      onClose={onClose}
      onMinimize={onMinimize}
      onRestore={onRestore}
      minWidth={MIN_WORKSPACE_WIDTH}
      minHeight={MIN_WORKSPACE_HEIGHT}
      width={width}
      height={height}
      fixedLeft={fixedLeft}
      topOffset={WINDOWS_TOP_OFFSET}
      onSizeChange={({ width: resizedWidth }) => {
        setHasUserResized(true);
        setWorkspaceWidth(resizedWidth);
      }}
    >
      <Box
        data-testid="kaiylee-workspace-layout"
        data-layout={layout}
        sx={{
          height: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: isNarrow ? 'column' : 'row',
          overflowX: 'hidden',
          overflowY: isNarrow ? 'auto' : 'hidden',
        }}
      >
        <Box
          data-testid="kaiylee-chat-pane"
          sx={{
            flex: chatFlex,
            minWidth: chatMinWidth,
            width: isNarrow ? '100%' : 'auto',
            height: isNarrow ? 'auto' : '100%',
            borderRight: isNarrow ? 'none' : '1px solid rgba(0,0,0,0.10)',
            borderBottom: isNarrow ? '1px solid rgba(0,0,0,0.10)' : 'none',
          }}
        >
          <KaiyleeChatPanel
            open
            onClose={onClose}
            draft={draft}
            onDraftChange={onDraftChange}
            onPreviewChange={setOfferPreview}
            onWorkspaceModeChange={setWorkspaceMode}
            businessId={businessId}
            onOfferDataRefresh={onOfferDataRefresh}
            onConfigureOffer={onConfigureOffer}
            variant="embedded"
          />
        </Box>
        <Box
          data-testid="kaiylee-preview-pane"
          sx={{
            flex: isNarrow ? '0 0 100%' : '1 1 auto',
            minWidth: 0,
            minHeight: isNarrow ? 600 : 0,
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {contextLabel && (
            <Box
              role="status"
              aria-label="Offer preview context"
              sx={{
                flex: '0 0 auto',
                display: 'flex',
                alignItems: 'baseline',
                gap: 1,
                px: 2,
                py: 1,
                borderBottom: '1px solid rgba(14,117,189,0.16)',
                bgcolor: 'rgba(14,117,189,0.05)',
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 750, color: '#0e75bd' }}>
                {contextLabel}
              </Typography>
            </Box>
          )}
          <Box sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
            <OfferPreviewCard
              details={details}
              businessId={businessId}
              useBusinessDefault={offerPreview
                ? offerPreview.kind !== 'PREVIOUS_OFFER'
                : !['EDITING_OFFER', 'VIEWING_OFFER'].includes(workspaceMode)}
              variant="embedded"
              showEmptyFields
              compact={!isWide}
              showBusinessAndType={false}
              fillAvailableHeight
            />
          </Box>
        </Box>
      </Box>
    </ResizableWindow>
  );
}
